import { calculatorFishingSourceSignalPatchV1 as fishingSourceSignalPatch, calculatorFishingSourcesEqual as fishingSourcesEqual, parseCalculatorFishingSourceV1 as parseFishingSource } from "./components/calculator-fishing-source.js";
import { calculatorAcceptedFixedControlSignalPatchV1 as acceptedFixedControlSignalPatch, calculatorCustomTradeDestinationSignalValueV1 as canonicalCustomTradeDestination } from "./components/calculator-fixed-control-signals.js";
import { CALCULATOR_ENGINE_MODEL_COMMITTED_EVENT as MODEL_COMMITTED_EVENT, CALCULATOR_ENGINE_MODEL_REVISION_ACTIVATED_EVENT as MODEL_REVISION_ACTIVATED_EVENT } from "./components/calculator-engine.js";
import { browserAuthorshipProfileRepository } from "./authorship-profile.js";

(function (global) {
  "use strict";

  const PROJECTION_EVENT = "fishystuff:calculator-engine-projection";
  const STATUS_EVENT = "fishystuff:calculator-engine-status";
  const ENGINE_CONNECTED_EVENT = "fishystuff:calculator-engine-connected";
  const PRESENTATION_EVENT = "fishystuff:calculator-presentation";
  const PRESENTER_READY_EVENT = "fishystuff:calculator-projection-presenter-ready";
  const LAYER_ACTIVATED_EVENT = "fishystuff:calculator-user-layer-activated";
  const LAYER_CONFIRMED_EVENT = "fishystuff:calculator-user-layer-confirmed";
  const SECTION_LAYOUT_CHANGE_EVENT = "fishystuff:calculator-section-layout-change";
  const SCENARIO_INTENT_EVENT = "fishystuff:calculator-scenario-intent";
  const MODEL_ITEMS_REQUEST_EVENT = "fishystuff:calculator-model-items-requested";
  const INSPECTOR_ITEM_SELECTED_EVENT = "fishystuff:calculator-layer-item-selected";
  const RATE_ALTERNATIVE_SELECTED_EVENT =
    "fishystuff:calculator-rate-alternative-selected";
  const RATE_ALTERNATIVE_OVERRIDES_SIGNAL = "_rate_alternative_overrides";
  const USER_LAYER_PRESET_FACADE_NAME = "__fishystuffCalculatorUserLayerV4Presets";
  const SCHEMA_VERSION = 1;
  const MAX_SUPPLEMENTAL_ITEM_IDS = 160;
  const CUSTOM_TRADE_DESTINATION_PREFIX = "custom:";
  const TRADE_ROUTE_POLICY_PRESERVE = "preserve";
  const TRADE_ROUTE_POLICY_RESELECT_FOR_ZONE = "reselectForZone";
  const USER_LAYER_VALIDATION_BOUNDARIES = new Set([
    "initialization",
    "modelRevision",
  ]);
  const USER_OPERATION_CREATION_ACTIONS = new Set([
    "addItem",
    "setItemPresence",
    "setItemGroupMembership",
    "setGroupPresence",
    "setItemRate",
    "setItemQuantity",
    "setItemQuantityDefault",
    "setGroupRate",
    "setItemPrice",
    "refuteEvidence",
  ]);
  const SOURCE_BACKED_SIGNAL_KEYS = new Set([
    "fishingMode",
    "rodZone",
    "harpoonZone",
    "hotspot",
    "rod",
    "float",
    "chair",
    "lightstone_set",
    "backpack",
    "_outfit_slots",
    "_food_slots",
    "_buff_slots",
    "outfit",
    "food",
    "buff",
    "targetFish",
    "tradeOriginRegion",
    "tradeDestinationNpc",
    "_pet1_skill_slot1",
    "_pet1_skill_slot2",
    "_pet1_skill_slot3",
    "_pet2_skill_slot1",
    "_pet2_skill_slot2",
    "_pet2_skill_slot3",
    "_pet3_skill_slot1",
    "_pet3_skill_slot2",
    "_pet3_skill_slot3",
    "_pet4_skill_slot1",
    "_pet4_skill_slot2",
    "_pet4_skill_slot3",
    "_pet5_skill_slot1",
    "_pet5_skill_slot2",
    "_pet5_skill_slot3",
  ]);
  const SOURCE_BACKED_PET_FIELDS = new Set([
    "kind",
    "displayLabel",
    "basedOn",
    "pet",
    "tier",
    "special",
    "talent",
    "skills",
  ]);
  const SCENARIO_SIGNAL_KEYS = new Set([
    ...SOURCE_BACKED_SIGNAL_KEYS,
    "level",
    "lifeskill_level",
    "mastery",
    "resources",
    "trade_level",
    "tradeDistanceBonus",
    "tradePriceCurve",
    "applyTradeModifiers",
    "active",
    "catchTimeActive",
    "catchTimeAfk",
    "timespanAmount",
    "timespanUnit",
    "targetFishAmount",
    "targetFishPmfCount",
    "discardGrade",
    "brand",
  ]);
  const SCENARIO_PET_FIELDS = new Set([
    ...SOURCE_BACKED_PET_FIELDS,
    "packLeader",
  ]);

  function isRecord(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
  }

  function parseLootRateDisplayMode(value, path) {
    const contract = global.__fishystuffLootRateDisplayModeContract;
    if (!contract || typeof contract.parse !== "function") {
      throw new Error("Loot rate display mode contract is unavailable");
    }
    return contract.parse(value ?? contract.NORMALIZED, path);
  }

  function clone(value) {
    return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
  }

  function userOperationMetadata(identity) {
    if (!isRecord(identity)) {
      throw new TypeError("calculator user-operation identity is malformed");
    }
    return Object.freeze({
      confidence: "guessed",
      author: Object.freeze({
        id: requiredString(identity.id, "calculator user-operation author ID"),
        displayName: requiredString(
          identity.displayName,
          "calculator user-operation author display name",
        ),
        handle: requiredString(identity.handle, "calculator user-operation author handle"),
      }),
      createdAtUtc: new Date().toISOString(),
    });
  }

  function authoredUserOperationAction(action, identity) {
    if (!isRecord(action) || !USER_OPERATION_CREATION_ACTIONS.has(action.kind)) {
      return action;
    }
    return Object.freeze({
      ...action,
      metadata: userOperationMetadata(identity),
    });
  }

  function mergePatch(target, patch) {
    if (!isRecord(target) || !isRecord(patch)) {
      return target;
    }
    for (const [key, value] of Object.entries(patch)) {
      if (isRecord(value) && isRecord(target[key])) {
        mergePatch(target[key], value);
      } else {
        target[key] = clone(value);
      }
    }
    return target;
  }

  function sameValue(left, right) {
    return JSON.stringify(left) === JSON.stringify(right);
  }

  function outputIdKey(outputId) {
    if (outputId.kind === "summary") return `summary:${outputId.metric}`;
    if (outputId.kind === "stat") return `stat:${outputId.stat}`;
    if (outputId.kind === "timeline") return `timeline:${outputId.segment}`;
    return outputId.kind;
  }

  function outputSelectors(outputId) {
    if (outputId.kind === "summary") {
      return [
        `[data-calculator-summary-metric="${outputId.metric}"]`,
        `[data-calculator-summary-breakdown="${outputId.metric}"]`,
      ];
    }
    if (outputId.kind === "stat") {
      return [
        `[data-calculator-stat-kind="${outputId.stat}"]`,
        `[data-calculator-stat-value="${outputId.stat}"]`,
      ];
    }
    if (outputId.kind === "timeline") return ["#fishing-timeline"];
    return {
      target: ["#calculator-target-fish-panel"],
      lootDistribution: ["[data-calculator-presenter^=\"distribution:\"]"],
      lootSankey: ["[data-calculator-presenter^=\"loot-sankey:\"]"],
      trade: ["[data-calculator-section-id=\"trade\"]"],
      provenance: ["[data-calculator-section-id=\"layers\"]"],
    }[outputId.kind] ?? [];
  }

  function outputStatePriority(state) {
    return { current: 0, stale: 1, unavailable: 2, invalid: 3 }[state] ?? 3;
  }

  function annotateOutputValidity(documentTarget, validity) {
    const previouslyAnnotated = documentTarget.querySelectorAll?.(
      "[data-calculator-output-id]",
    ) ?? [];
    for (const node of previouslyAnnotated) {
      node.removeAttribute?.("data-calculator-output-id");
      node.removeAttribute?.("data-calculator-output-validity");
      node.removeAttribute?.("aria-invalid");
    }
    const annotations = new Map();
    for (const entry of validity ?? []) {
      const outputKey = outputIdKey(entry.outputId);
      for (const selector of outputSelectors(entry.outputId)) {
        for (const node of documentTarget.querySelectorAll?.(selector) ?? []) {
          const current = annotations.get(node) ?? { outputIds: [], state: "current" };
          current.outputIds.push(outputKey);
          if (outputStatePriority(entry.state) > outputStatePriority(current.state)) {
            current.state = entry.state;
          }
          annotations.set(node, current);
        }
      }
    }
    for (const [node, annotation] of annotations) {
      node.setAttribute?.(
        "data-calculator-output-id",
        [...new Set(annotation.outputIds)].join(" "),
      );
      node.setAttribute?.("data-calculator-output-validity", annotation.state);
      if (annotation.state === "invalid") node.setAttribute?.("aria-invalid", "true");
    }
  }

  function issueInputSelector(path) {
    const scenarioPath = String(path ?? "").replace(/^scenario\./, "");
    const direct = {
      fishingLevel: "level",
      lifeskillLevel: "lifeskill_level",
      mastery: "mastery",
      resourcesPercent: "resources",
      "equipment.rodId": "rod",
      "equipment.floatId": "float",
      "equipment.chairId": "chair",
      "equipment.lightstoneSetId": "lightstone_set",
      "equipment.backpackId": "backpack",
      "equipment.outfitIds": "_outfit_slots",
      "equipment.foodIds": "_food_slots",
      "equipment.buffIds": "_buff_slots",
      "trade.tradeLevel": "trade_level",
      "trade.originId": "tradeOriginRegion",
      "trade.destination": "tradeDestinationNpc",
      "trade.priceCurvePercent": "tradePriceCurve",
      "trade.applyModifiers": "applyTradeModifiers",
      "session.activeFishing": "active",
      "session.activeCatchTimeSeconds": "catchTimeActive",
      "session.afkCatchTimeSeconds": "catchTimeAfk",
      "session.timespanAmount": "timespanAmount",
      "session.timespanUnit": "timespanUnit",
      "target.itemId": "targetFish",
      "target.amount": "targetFishAmount",
      "target.pmfTailCount": "targetFishPmfCount",
      discardGrade: "discardGrade",
      branded: "brand",
    }[scenarioPath];
    if (direct) return `[data-bind="${direct}"]`;
    if (scenarioPath.startsWith("fishingSource")) {
      return "[data-bind=\"fishingMode\"]";
    }
    if (scenarioPath.startsWith("userLayer") || path === "userLayer") {
      return "[data-calculator-data-workspace]";
    }
    return null;
  }

  function annotateInputIssues(documentTarget, issues) {
    for (const node of documentTarget.querySelectorAll?.("[data-calculator-inline-issue]") ?? []) {
      node.remove?.();
    }
    for (const node of documentTarget.querySelectorAll?.("[data-calculator-input-invalid]") ?? []) {
      node.removeAttribute?.("data-calculator-input-invalid");
      node.removeAttribute?.("aria-invalid");
    }
    for (const issue of issues ?? []) {
      const petMatch = /^pet([1-5])\.(.+)$/.exec(issue.path);
      const input = petMatch
        ? documentTarget.querySelector?.(`[data-pet-slot="${petMatch[1]}"]`)
        : documentTarget.querySelector?.(issueInputSelector(issue.path));
      const owner = input?.closest?.("fieldset") ?? input;
      if (!owner) continue;
      input.setAttribute?.("data-calculator-input-invalid", issue.path);
      input.setAttribute?.("aria-invalid", "true");
      if (!documentTarget.createElement || !owner.appendChild) continue;
      const explanation = documentTarget.createElement("p");
      explanation.setAttribute("data-calculator-inline-issue", issue.path);
      explanation.setAttribute("role", "alert");
      explanation.className = "text-sm text-error";
      explanation.textContent = `${issue.path}: ${issue.message ?? issue.reason}`;
      owner.appendChild(explanation);
    }
  }

  function signalPatchMatches(expected, actual) {
    if (!isRecord(actual)) {
      // Datastar projects an absent nullable control value through a custom
      // element as the form-value sentinel. Both values reconstruct the same
      // optional scenario field, so this is still an acknowledgement of the
      // accepted model revision rather than a new user transition.
      if (expected === null && actual === "") {
        return true;
      }
      return sameValue(actual, expected);
    }
    if (!isRecord(expected)) {
      return false;
    }
    for (const [key, value] of Object.entries(actual)) {
      if (!Object.prototype.hasOwnProperty.call(expected, key)) {
        return false;
      }
      if (!signalPatchMatches(expected[key], value)) {
        return false;
      }
    }
    return true;
  }

  function requiredString(value, name) {
    if (typeof value !== "string" || value.trim() === "") {
      throw new TypeError(`${name} must be a non-empty string`);
    }
    return value.trim();
  }

  function nullableString(value) {
    if (value === null || value === undefined) {
      return null;
    }
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
  }

  function canonicalRateAlternativeOverrides(value, name = "rate alternative overrides") {
    if (!Array.isArray(value)) {
      throw new TypeError(`${name} must be an array`);
    }
    const byFamily = new Map();
    for (const [index, entry] of value.entries()) {
      if (!isRecord(entry)) {
        throw new TypeError(`${name}[${index}] must be an object`);
      }
      const fields = Object.keys(entry).sort();
      if (fields.join(",") !== "alternativeId,familyId") {
        throw new TypeError(
          `${name}[${index}] fields must be exactly alternativeId and familyId`,
        );
      }
      const override = Object.freeze({
        familyId: requiredString(entry.familyId, `${name}[${index}].familyId`),
        alternativeId: requiredString(
          entry.alternativeId,
          `${name}[${index}].alternativeId`,
        ),
      });
      if (byFamily.has(override.familyId)) {
        throw new TypeError(`${name} contains duplicate family ${override.familyId}`);
      }
      byFamily.set(override.familyId, override);
    }
    return Object.freeze([...byFamily.values()].sort((left, right) => (
      left.familyId.localeCompare(right.familyId)
    )));
  }

  function scenarioWithRateAlternativeOverrides(scenario, overrides) {
    return Object.freeze({
      ...scenario,
      rateAlternativeOverrides: canonicalRateAlternativeOverrides(overrides),
    });
  }

  function customTradeDistance(value) {
    const normalized = String(value ?? "").trim();
    if (!normalized.startsWith(CUSTOM_TRADE_DESTINATION_PREFIX)) {
      return undefined;
    }
    const distance = Number(normalized.slice(CUSTOM_TRADE_DESTINATION_PREFIX.length).trim());
    return Number.isFinite(distance) && distance >= 0 ? distance : null;
  }

  function scenarioForCustomTradeSelection(scenario, patch) {
    if (!Object.prototype.hasOwnProperty.call(patch, "tradeDestinationNpc")) {
      return { scenario, distanceSignal: undefined };
    }
    const selectedDistance = customTradeDistance(patch.tradeDestinationNpc);
    if (selectedDistance === undefined) {
      return { scenario, distanceSignal: undefined };
    }
    if (selectedDistance === null) {
      throw new TypeError("custom trade destination must contain a finite non-negative distance");
    }
    const currentDistance = scenario?.trade?.destination?.kind === "custom"
      ? Number(scenario.trade.destination.distanceBonusPercent)
      : Number.NaN;
    if (
      Number.isFinite(currentDistance)
      && canonicalCustomTradeDestination(currentDistance)
        === canonicalCustomTradeDestination(selectedDistance)
    ) {
      return { scenario, distanceSignal: undefined };
    }
    return {
      scenario: {
        ...scenario,
        trade: {
          ...scenario.trade,
          destination: {
            kind: "custom",
            distanceBonusPercent: selectedDistance,
          },
        },
      },
      distanceSignal: selectedDistance,
    };
  }

  function canonicalItemIds(values, name = "requested item IDs") {
    if (!Array.isArray(values)) {
      throw new TypeError(`${name} must be an array`);
    }
    const itemIds = values.map((value, index) => {
      const itemId = Number(value);
      if (!Number.isInteger(itemId) || itemId < 1 || itemId > 4_294_967_295) {
        throw new TypeError(`${name}[${index}] must be a positive 32-bit item ID`);
      }
      return itemId;
    });
    itemIds.sort((left, right) => left - right);
    const canonical = itemIds.filter((value, index) => index === 0 || value !== itemIds[index - 1]);
    if (canonical.length > MAX_SUPPLEMENTAL_ITEM_IDS) {
      throw new TypeError(`${name} may contain at most ${MAX_SUPPLEMENTAL_ITEM_IDS} identities`);
    }
    return canonical;
  }

  function userLayerItemIds(userLayer, fishingSource) {
    if (!isRecord(userLayer) || !Array.isArray(userLayer.operations)) {
      return [];
    }
    const activeFishingSource = parseFishingSource(fishingSource);
    return userLayer.operations.flatMap((operation) => {
      if (!isRecord(operation)) {
        return [];
      }
      const activatesItem = operation.kind === "groupMembership"
        || operation.kind === "itemPresence";
      if (
        !activatesItem
        || !fishingSourcesEqual(operation.fishingSource, activeFishingSource)
      ) {
        return [];
      }
      return Number.isInteger(operation.itemId) ? [operation.itemId] : [];
    });
  }

  function patchChangesSourceBackedSelection(patch) {
    if (!isRecord(patch)) {
      return false;
    }
    for (const key of Object.keys(patch)) {
      if (SOURCE_BACKED_SIGNAL_KEYS.has(key)) {
        return true;
      }
      if (/^pet[1-5]$/.test(key) && isRecord(patch[key])) {
        if (Object.keys(patch[key]).some((field) => SOURCE_BACKED_PET_FIELDS.has(field))) {
          return true;
        }
      }
    }
    return false;
  }

  function patchChangesScenario(patch) {
    if (!isRecord(patch)) {
      return false;
    }
    for (const key of Object.keys(patch)) {
      if (SCENARIO_SIGNAL_KEYS.has(key)) {
        return true;
      }
      if (/^pet[1-5]$/.test(key) && isRecord(patch[key])) {
        if (Object.keys(patch[key]).some((field) => SCENARIO_PET_FIELDS.has(field))) {
          return true;
        }
      }
    }
    return false;
  }

  class CalculatorEnginePageController {
    constructor({
      getSignals,
      language,
      resolveApiUrl,
      patchSignals = null,
      fetch: fetchImpl = global.fetch?.bind(global),
      document: documentTarget = global.document,
      onProjection = null,
      navigateWorkspace,
      authorshipProfileRepository = browserAuthorshipProfileRepository(),
    }) {
      if (typeof getSignals !== "function") {
        throw new TypeError("getSignals must be a function");
      }
      if (typeof language !== "function") {
        throw new TypeError("language must be a function");
      }
      if (typeof resolveApiUrl !== "function") {
        throw new TypeError("resolveApiUrl must be a function");
      }
      if (patchSignals !== null && typeof patchSignals !== "function") {
        throw new TypeError("patchSignals must be a function or null");
      }
      if (
        !authorshipProfileRepository
        || typeof authorshipProfileRepository.state !== "function"
        || typeof authorshipProfileRepository.historicalAuthor !== "function"
        || typeof authorshipProfileRepository.ensureAuthor !== "function"
      ) {
        throw new TypeError(
          "authorshipProfileRepository has an incompatible contract",
        );
      }
      this.getSignals = getSignals;
      this.language = language;
      this.resolveApiUrl = resolveApiUrl;
      this.patchSignals = patchSignals;
      this.fetch = fetchImpl;
      this.document = documentTarget;
      this.onProjection = onProjection;
      this.navigateWorkspace = navigateWorkspace;
      this.authorshipProfileRepository = authorshipProfileRepository;
      this.engine = null;
      this.userLayer = null;
      this.confirmedUserLayer = null;
      this.initialUserLayerDocument = null;
      this.userLayerPresetFacade = null;
      this.sourceReference = null;
      this.staticPresentation = null;
      this.inspectorDataPresentation = null;
      this.lootSort = Object.freeze({ by: "price", order: "desc" });
      this.lootRateDisplayMode = parseLootRateDisplayMode(
        undefined,
        "calculator loot rate display mode",
      );
      this.presentationAdapter = null;
      this.latestProjection = null;
      this.modelRequestSequence = 0;
      this.pendingModelRequestedItemIds = Object.freeze([]);
      this.pendingCommittedModelSignalPatch = null;
      this.validationRequestSequence = 0;
      this.pendingValidationRequest = null;
      this.initialModelRevision = null;
      this.lastBoundaryRevision = null;
      this.boundaryInspectorPresent = false;
      this.inspectorSelectedItemId = null;
      this.inspectorCustomItemIds = Object.freeze([]);
      this.connected = false;
      this.lastRecoveryEventKey = null;
      this.lifecycleListening = false;
      this._projectionHandler = (event) => this._handleProjection(event);
      this._statusHandler = (event) => this._handleStatus(event);
      this._presentationHandler = (event) => this._handlePresentation(event);
      this._modelCommittedHandler = (event) => this._handleModelCommitted(event);
      this._modelRevisionActivatedHandler = (event) => (
        this._handleModelRevisionActivated(event)
      );
      this._presenterReadyHandler = () => this._installPresenter(this.staticPresentation);
      this._layerActivatedHandler = (event) => this._handleLayerDocument(event);
      this._layerConfirmedHandler = (event) => this._handleConfirmedLayerDocument(event);
      this._sectionLayoutHandler = (event) => this._handleSectionLayout(event);
      this._scenarioIntentHandler = (event) => this._handleScenarioIntent(event);
      this._inspectorItemSelectedHandler = (event) => this._handleInspectorItemSelected(event);
      this._rateAlternativeSelectedHandler = (event) => (
        this._handleRateAlternativeSelected(event)
      );
      this._modelItemsRequestHandler = (event) => this._handleModelItemsRequest(event);
      this._inspectorDraftPreparer = (document, action) => (
        this._prepareInspectorDraft(document, action)
      );
      this._inspectorDocumentValidator = ({ boundary, userLayer }) => (
        this.validateUserLayer({ boundary, userLayer })
      );
      this._presetDocumentActivationHandler = (document) => (
        this._activatePresetUserLayerDocument(document)
      );
      this._engineConnectedHandler = (event) => {
        if (this.document.getElementById?.("calculator-engine") === event.target) {
          this.connect();
        }
      };
    }

    _applyInspectorDraftWithAuthor(document, action, identity) {
      return this.engine.applyUserLayerDraft(
        document,
        authoredUserOperationAction(action, identity),
      );
    }

    _prepareInspectorDraft(document, action) {
      if (!isRecord(action) || !USER_OPERATION_CREATION_ACTIONS.has(action.kind)) {
        return this.engine.applyUserLayerDraft(document, action);
      }
      const currentIdentity = this.authorshipProfileRepository.historicalAuthor();
      if (currentIdentity) {
        return this._applyInspectorDraftWithAuthor(document, action, currentIdentity);
      }
      return this._ensureHistoricalAuthor().then((identity) => (
        this._applyInspectorDraftWithAuthor(document, action, identity)
      ));
    }

    async _ensureHistoricalAuthor() {
      const currentIdentity = this.authorshipProfileRepository.historicalAuthor();
      if (currentIdentity) {
        return currentIdentity;
      }
      const repositoryState = this.authorshipProfileRepository.state();
      if (repositoryState.kind !== "missing") {
        throw new Error(
          `Authorship profile state is ${repositoryState.kind}; the authored edit was not applied.`,
        );
      }
      const ensured = await this.authorshipProfileRepository.ensureAuthor({
        expectedRevision: null,
      });
      const identity = this.authorshipProfileRepository.historicalAuthor();
      if (
        identity
        && (ensured?.kind === "ready" || ensured?.kind === "conflict")
      ) {
        return identity;
      }
      if (!ensured || !["ready", "conflict"].includes(ensured.kind)) {
        throw new Error(
          `Authorship profile creation reached ${ensured?.kind || "unknown"}; the authored edit was not applied.`,
        );
      }
      throw new Error(
        "Authorship profile projection is unavailable after creation.",
      );
    }

    connect() {
      if (!this.lifecycleListening) {
        this.document.addEventListener?.(
          ENGINE_CONNECTED_EVENT,
          this._engineConnectedHandler,
        );
        this.lifecycleListening = true;
      }
      const engine = this.document.getElementById?.("calculator-engine") ?? null;
      if (!engine) {
        return false;
      }
      if (this.connected && this.engine === engine) {
        return true;
      }
      this.engine = engine;
      if (!this.connected) {
        this.document.addEventListener?.(PROJECTION_EVENT, this._projectionHandler);
        this.document.addEventListener?.(STATUS_EVENT, this._statusHandler);
        this.document.addEventListener?.(PRESENTATION_EVENT, this._presentationHandler);
        this.document.addEventListener?.(
          MODEL_REVISION_ACTIVATED_EVENT,
          this._modelRevisionActivatedHandler,
        );
        this.document.addEventListener?.(MODEL_COMMITTED_EVENT, this._modelCommittedHandler);
        this.document.addEventListener?.(LAYER_ACTIVATED_EVENT, this._layerActivatedHandler);
        this.document.addEventListener?.(LAYER_CONFIRMED_EVENT, this._layerConfirmedHandler);
        this.document.addEventListener?.(SECTION_LAYOUT_CHANGE_EVENT, this._sectionLayoutHandler);
        this.document.addEventListener?.(SCENARIO_INTENT_EVENT, this._scenarioIntentHandler);
        this.document.addEventListener?.(
          INSPECTOR_ITEM_SELECTED_EVENT,
          this._inspectorItemSelectedHandler,
        );
        this.document.addEventListener?.(
          RATE_ALTERNATIVE_SELECTED_EVENT,
          this._rateAlternativeSelectedHandler,
        );
        this.document.addEventListener?.(MODEL_ITEMS_REQUEST_EVENT, this._modelItemsRequestHandler);
        global.addEventListener?.(PRESENTER_READY_EVENT, this._presenterReadyHandler, {
          once: true,
        });
        this.connected = true;
      }
      this._readBoundarySignals();
      this._bindUserLayerPresetFacade();
      this._syncSectionLayout();
      this._installPresenter(engine.presentation, engine.modelRevision);
      this._wireInspector();
      if (engine.projection && engine.projection !== this.latestProjection) {
        this._present(engine.projection);
      }
      this._validateActivatedRevision();
      return true;
    }

    disconnect() {
      if (this.lifecycleListening) {
        this.document.removeEventListener?.(
          ENGINE_CONNECTED_EVENT,
          this._engineConnectedHandler,
        );
        this.lifecycleListening = false;
      }
      if (!this.connected) {
        return;
      }
      this.document.removeEventListener?.(PROJECTION_EVENT, this._projectionHandler);
      this.document.removeEventListener?.(STATUS_EVENT, this._statusHandler);
      this.document.removeEventListener?.(PRESENTATION_EVENT, this._presentationHandler);
      this.document.removeEventListener?.(
        MODEL_REVISION_ACTIVATED_EVENT,
        this._modelRevisionActivatedHandler,
      );
      this.document.removeEventListener?.(MODEL_COMMITTED_EVENT, this._modelCommittedHandler);
      this.document.removeEventListener?.(LAYER_ACTIVATED_EVENT, this._layerActivatedHandler);
      this.document.removeEventListener?.(LAYER_CONFIRMED_EVENT, this._layerConfirmedHandler);
      this.document.removeEventListener?.(SECTION_LAYOUT_CHANGE_EVENT, this._sectionLayoutHandler);
      this.document.removeEventListener?.(SCENARIO_INTENT_EVENT, this._scenarioIntentHandler);
      this.document.removeEventListener?.(
        INSPECTOR_ITEM_SELECTED_EVENT,
        this._inspectorItemSelectedHandler,
      );
      this.document.removeEventListener?.(
        RATE_ALTERNATIVE_SELECTED_EVENT,
        this._rateAlternativeSelectedHandler,
      );
      this.document.removeEventListener?.(MODEL_ITEMS_REQUEST_EVENT, this._modelItemsRequestHandler);
      global.removeEventListener?.(PRESENTER_READY_EVENT, this._presenterReadyHandler);
      this._cancelPendingValidation();
      this.connected = false;
      this.engine = null;
      this.pendingCommittedModelSignalPatch = null;
    }

    _cancelPendingValidation() {
      const pending = this.pendingValidationRequest;
      if (!pending) {
        return false;
      }
      this.pendingValidationRequest = null;
      pending.abortController.abort();
      return true;
    }

    _supersededValidationOutcome({ boundary, modelRevision, requestId }) {
      return Object.freeze({
        outcome: "superseded",
        schemaVersion: SCHEMA_VERSION,
        requestId,
        modelRevision,
        boundary,
      });
    }

    validateUrl() {
      return this.resolveApiUrl("/api/v1/calculator/datastar/validate");
    }

    itemSearchUrl({ modelRevision }) {
      const current = this.language();
      const params = new URLSearchParams({
        lang: requiredString(current.apiLang, "data language"),
        locale: requiredString(current.locale, "locale"),
        kind: "inspector_item",
        model_revision: requiredString(modelRevision, "model revision"),
        source_reference: requiredString(this.sourceReference, "calculator source reference"),
        results_id: "calculator-loot-item-picker-results",
      });
      return this.resolveApiUrl(
        `/api/v1/calculator/datastar/option-search?${params.toString()}`,
      );
    }

    handleScenarioIntent(scenario, patch) {
      if (!isRecord(scenario) || !isRecord(patch) || !this.connect()) {
        return false;
      }
      if (typeof patch._calculatorSourceReference === "string") {
        this.sourceReference = requiredString(
          patch._calculatorSourceReference,
          "calculator source reference",
        );
      }
      if (Object.prototype.hasOwnProperty.call(patch, "_calculator_ui")) {
        this._syncSectionLayout(patch);
      }

      const intendedPatch = this._withoutCommittedModelSignalPatch(patch);

      if (!patchChangesScenario(intendedPatch)) {
        return false;
      }
      let selected;
      try {
        selected = scenarioForCustomTradeSelection(scenario, intendedPatch);
      } catch (error) {
        this.engine.failLocalEvaluation?.(error);
        return false;
      }
      const sourceBackedSelectionChanged = patchChangesSourceBackedSelection(
        intendedPatch,
      );
      let fishingSourceChanged = false;
      if (sourceBackedSelectionChanged) {
        try {
          fishingSourceChanged = !fishingSourcesEqual(
            selected.scenario.fishingSource,
            this.engine.scenario?.fishingSource,
          );
        } catch (error) {
          this.engine.failLocalEvaluation?.(error);
          return false;
        }
      }
      const intendedScenario = scenarioWithRateAlternativeOverrides(
        selected.scenario,
        fishingSourceChanged
          ? []
          : this.engine.scenario?.rateAlternativeOverrides ?? [],
      );
      if (sameValue(intendedScenario, this.engine.scenario)) {
        this._syncCustomTradeDistanceSignal(selected.distanceSignal);
        return false;
      }
      // Once any branch is non-current, the retained local model cannot prove
      // that a later edit is independent. Route the next transition through
      // the engine-owned dependency analysis so affected outputs are unioned
      // by the authoritative response.
      if (this.engine.hasNonCurrentOutputs) {
        const requested = this._requestModel(
          intendedScenario,
          null,
          TRADE_ROUTE_POLICY_PRESERVE,
        );
        if (requested) {
          this._syncCustomTradeDistanceSignal(selected.distanceSignal);
        }
        return requested;
      }
      if (sourceBackedSelectionChanged) {
        if (fishingSourceChanged) {
          this.inspectorCustomItemIds = Object.freeze([]);
          this.inspectorSelectedItemId = null;
        }
        if (fishingSourceChanged && typeof this.patchSignals === "function") {
          this.patchSignals(
            { [RATE_ALTERNATIVE_OVERRIDES_SIGNAL]: [] },
            { replaceTopLevel: true },
          );
        }
        const requested = this._requestModel(
          intendedScenario,
          null,
          fishingSourceChanged
            ? TRADE_ROUTE_POLICY_RESELECT_FOR_ZONE
            : TRADE_ROUTE_POLICY_PRESERVE,
        );
        if (requested) {
          this._syncCustomTradeDistanceSignal(selected.distanceSignal);
        }
        return requested;
      }
      this.engine.scenario = intendedScenario;
      this._syncCustomTradeDistanceSignal(selected.distanceSignal);
      return true;
    }

    _withoutCommittedModelSignalPatch(patch) {
      const pending = this.pendingCommittedModelSignalPatch;
      if (!isRecord(pending?.expected)) {
        return patch;
      }
      const intended = { ...patch };
      for (const [key, value] of Object.entries(patch)) {
        if (!Object.prototype.hasOwnProperty.call(pending.expected, key)) {
          continue;
        }
        if (!signalPatchMatches(pending.expected[key], value)) {
          this.pendingCommittedModelSignalPatch = null;
          return patch;
        }
        delete intended[key];
      }
      return intended;
    }

    _syncCustomTradeDistanceSignal(distance) {
      if (distance === undefined || typeof this.patchSignals !== "function") {
        return false;
      }
      if (Number(this.getSignals()?.tradeDistanceBonus) === distance) {
        return false;
      }
      this.patchSignals({ tradeDistanceBonus: distance }, { replaceTopLevel: true });
      return true;
    }

    async validateUserLayer({ boundary, userLayer } = {}) {
      if (!this.connect()) {
        throw new Error("calculator engine is unavailable");
      }
      const authorityBoundary = requiredString(boundary, "validation boundary");
      if (!USER_LAYER_VALIDATION_BOUNDARIES.has(authorityBoundary)) {
        throw new TypeError(
          `unsupported calculator user-layer validation boundary: ${authorityBoundary}`,
        );
      }
      if (!isRecord(userLayer)) {
        throw new TypeError("validation requires a typed user layer");
      }
      if (typeof this.fetch !== "function") {
        throw new TypeError("calculator validation requires fetch()");
      }
      const candidateUserLayer = clone(userLayer);
      const model = this.engine.model;
      const scenario = this.engine.scenario ?? model?.defaultScenario;
      const sourceReference = requiredString(
        this.sourceReference,
        "calculator source reference",
      );
      const current = this.language();
      if (
        authorityBoundary === "initialization"
        || authorityBoundary === "modelRevision"
      ) {
        this.initialModelRevision ??= model.modelRevision;
        this.lastBoundaryRevision = model.modelRevision;
      }
      const requestId = `calculator-validation:${++this.validationRequestSequence}`;
      const modelRevision = requiredString(model.modelRevision, "modelRevision");
      const request = {
        schemaVersion: SCHEMA_VERSION,
        requestId,
        engineVersion: requiredString(model?.engineVersion, "engineVersion"),
        dataLanguage: requiredString(current.apiLang, "data language"),
        sourceReference,
        clientResolvedRevision: requiredString(
          model?.resolvedRevision,
          "resolvedRevision",
        ),
        selection: clone(model.selection),
        modelRevision,
        scenario: clone(scenario),
        userLayer: candidateUserLayer,
      };
      this._cancelPendingValidation();
      const abortController = new global.AbortController();
      const pendingRequest = Object.freeze({
        abortController,
        boundary: authorityBoundary,
        engine: this.engine,
        modelRevision,
        requestId,
      });
      this.pendingValidationRequest = pendingRequest;
      pendingRequest.engine.beginValidation(requestId);
      let response;
      try {
        const result = await this.fetch(this.validateUrl(), {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
          signal: abortController.signal,
        });
        response = await result.json();
        if (!result.ok && response?.outcome === undefined) {
          throw new Error(`calculator validation failed with HTTP ${result.status}`);
        }
      } catch (error) {
        if (
          abortController.signal.aborted
          || this.pendingValidationRequest !== pendingRequest
        ) {
          return this._supersededValidationOutcome(pendingRequest);
        }
        this.pendingValidationRequest = null;
        pendingRequest.engine.failValidation(requestId, error);
        throw error;
      }
      if (
        abortController.signal.aborted
        || this.pendingValidationRequest !== pendingRequest
      ) {
        return this._supersededValidationOutcome(pendingRequest);
      }
      this.pendingValidationRequest = null;
      if (!pendingRequest.engine.applyValidationResponse(response, {
        validatedUserLayer: response?.outcome === "validated"
          ? candidateUserLayer
          : undefined,
      })) {
        return this._supersededValidationOutcome(pendingRequest);
      }
      if (response.outcome === "validated") {
        this.userLayer = candidateUserLayer;
        this.confirmedUserLayer = clone(candidateUserLayer);
      }
      return response;
    }

    _desiredSupplementalItemIds(
      selectedItemIds = [],
      fishingSource = this.engine?.scenario?.fishingSource,
    ) {
      return canonicalItemIds([
        ...userLayerItemIds(this.userLayer, fishingSource),
        ...this.inspectorCustomItemIds,
        ...selectedItemIds,
      ]);
    }

    _modelLoadsItemId(itemId) {
      const operations = [
        ...(this.engine?.model?.baseFacts?.facts ?? []),
        ...(this.engine?.model?.sourceLayers ?? []).flatMap((layer) => layer.operations ?? []),
      ];
      return operations.some((operation) => (
        operation?.kind === "itemIdentity" && operation.itemId === itemId
      ));
    }

    _requestModel(
      scenario,
      requestedItemIds = null,
      tradeRoutePolicy = TRADE_ROUTE_POLICY_PRESERVE,
    ) {
      if (!isRecord(this.userLayer)) {
        this.engine.failLocalEvaluation?.(
          new Error("calculator model refresh requires a typed user layer"),
        );
        return false;
      }
      const current = this.language();
      const boundaryScenario = scenarioWithRateAlternativeOverrides(
        scenario,
        scenario.rateAlternativeOverrides ?? [],
      );
      const desiredItemIds = requestedItemIds ?? this._desiredSupplementalItemIds(
        this.inspectorSelectedItemId === null ? [] : [this.inspectorSelectedItemId],
        boundaryScenario.fishingSource,
      );
      const requestId = `calculator-model:${++this.modelRequestSequence}`;
      const request = {
        schemaVersion: SCHEMA_VERSION,
        requestId,
        dataLanguage: requiredString(current.apiLang, "data language"),
        locale: requiredString(current.locale, "locale"),
        sourceReference: nullableString(this.sourceReference),
        requestedItemIds: canonicalItemIds(desiredItemIds),
        tradeRoutePolicy,
        scenario: clone(boundaryScenario),
        confirmedScenario: clone(this.engine.confirmedScenario ?? boundaryScenario),
        userLayer: clone(this.userLayer),
      };
      this.pendingModelRequestedItemIds = Object.freeze([
        ...request.requestedItemIds,
      ]);
      this.engine.beginModelRequest(requestId, boundaryScenario);
      try {
        if (typeof this.engine.requestModel !== "function") {
          throw new TypeError("calculator engine is missing its declarative model request boundary");
        }
        this.engine.requestModel(request);
        return true;
      } catch (error) {
        this.engine.failModelRequest(request.requestId, error);
        return false;
      }
    }

    _syncActiveUserLayer() {
      if (isRecord(this.engine.userLayer)) {
        this.userLayer = clone(this.engine.userLayer);
        this.confirmedUserLayer ??= clone(this.engine.userLayer);
        this.initialUserLayerDocument ??= this._userLayerDocument(this.engine.userLayer);
      }
    }

    _userLayerDocument(userLayer = this.userLayer) {
      if (!isRecord(userLayer)) {
        return null;
      }
      return {
        documentVersion: 4,
        userLayer: clone(userLayer),
      };
    }

    _bindUserLayerPresetFacade() {
      const facade = global[USER_LAYER_PRESET_FACADE_NAME] ?? null;
      const initialDocument = this.initialUserLayerDocument
        ?? this._userLayerDocument(this.engine?.userLayer);
      if (
        !facade
        || typeof facade.isBound !== "function"
        || typeof facade.bind !== "function"
        || typeof facade.updateCurrentDocument !== "function"
        || typeof facade.reconcileCurrentDocument !== "function"
        || !initialDocument
      ) {
        return false;
      }
      const newlyBound = !facade.isBound();
      if (newlyBound) {
        facade.bind({
          initialDocument,
          currentDocument: initialDocument,
          activateDocument: this._presetDocumentActivationHandler,
        });
      }
      this.userLayerPresetFacade = facade;
      return true;
    }

    _updateUserLayerPresetDocument(document) {
      if (!this._bindUserLayerPresetFacade()) {
        return null;
      }
      return this.userLayerPresetFacade.reconcileCurrentDocument(document, {
        refresh: false,
      });
    }

    _reconcileUserLayerPresetDocument(document) {
      if (!this._bindUserLayerPresetFacade()) {
        return null;
      }
      return this.userLayerPresetFacade.reconcileCurrentDocument(document);
    }

    _activatePresetUserLayerDocument(document) {
      if (!isRecord(document?.userLayer)) {
        throw new TypeError("calculator preset activation requires a strict v4 document");
      }
      this.document.dispatchEvent?.(new global.CustomEvent(LAYER_ACTIVATED_EVENT, {
        detail: Object.freeze({
          source: "preset",
          document,
        }),
      }));
      const active = this._userLayerDocument(this.engine?.userLayer);
      if (!active || !sameValue(active.userLayer, document.userLayer)) {
        throw new Error("calculator preset activation did not become the active user layer");
      }
      return active;
    }

    _readBoundarySignals() {
      this._syncActiveUserLayer();
      const signals = this.getSignals() ?? {};
      if (typeof signals._calculatorSourceReference === "string") {
        this.sourceReference = signals._calculatorSourceReference;
      }
    }

    _syncSectionLayout(patch = null) {
      const signals = clone(this.getSignals() ?? {});
      if (isRecord(patch)) {
        mergePatch(signals, patch);
      }
      const ui = signals._calculator_ui;
      if (!isRecord(ui)) {
        return false;
      }
      const state = {
        workspaceTab: requiredString(ui.workspace_tab, "calculator workspace tab"),
        customLayout: clone(ui.custom_layout),
        customSections: clone(ui.custom_sections),
      };
      const lootSort = {
        by: requiredString(ui.loot_flow_sort_by || "price", "calculator loot sort field"),
        order: requiredString(
          ui.loot_flow_sort_order || "desc",
          "calculator loot sort order",
        ),
      };
      const lootRateDisplayMode = parseLootRateDisplayMode(
        ui.loot_rate_display_mode,
        "calculator loot rate display mode",
      );
      const lootRateDisplayModeChanged =
        this.lootRateDisplayMode !== lootRateDisplayMode;
      this.lootSort = Object.freeze(lootSort);
      this.lootRateDisplayMode = lootRateDisplayMode;
      const stacks = this.document.querySelectorAll?.("fishy-calculator-section-stack") ?? [];
      for (const stack of stacks) {
        if (!sameValue(stack.layoutState, state)) {
          stack.layoutState = clone(state);
        }
      }
      const inspectors = this.document.querySelectorAll?.(
        "fishy-calculator-layer-inspector-host",
      ) ?? [];
      for (const inspector of inspectors) {
        inspector.lootSort = lootSort;
        inspector.lootRateDisplayMode = lootRateDisplayMode;
        inspector.refreshProjection?.();
      }
      const sankeys = this.document.querySelectorAll?.("fishy-loot-sankey") ?? [];
      for (const sankey of sankeys) {
        sankey.sort = lootSort;
      }
      if (
        lootRateDisplayModeChanged
        && this.presentationAdapter
        && this.latestProjection
        && this.latestProjection.modelRevision === this.engine?.modelRevision
      ) {
        this._present(this.latestProjection);
      }
      return stacks.length > 0;
    }

    _wireInspector() {
      const inspectors = this.document.querySelectorAll?.("fishy-calculator-layer-inspector-host") ?? [];
      this.boundaryInspectorPresent = inspectors.length > 0;
      for (const inspector of inspectors) {
        if (inspector.model !== this.engine.model) {
          inspector.setEvaluationState(this.engine.model, this.engine.projection);
        } else if (inspector.projection !== this.engine.projection) {
          inspector.projection = this.engine.projection;
        }
        if (
          isRecord(this.userLayer)
          && !sameValue(inspector.userLayerDocument?.userLayer, this.userLayer)
        ) {
          inspector.userLayerDocument = {
            documentVersion: 4,
            userLayer: clone(this.userLayer),
          };
        }
        const boundaryReady = isRecord(this.engine.model)
          && isRecord(this.userLayer)
          && typeof this.sourceReference === "string";
        const draftPreparer = boundaryReady ? this._inspectorDraftPreparer : null;
        const documentValidator = boundaryReady ? this._inspectorDocumentValidator : null;
        const catalogSearchUrl = boundaryReady
          ? this.itemSearchUrl({ modelRevision: this.engine.modelRevision })
          : "";
        if (inspector.draftPreparer !== draftPreparer) {
          inspector.draftPreparer = draftPreparer;
        }
        if (inspector.documentValidator !== documentValidator) {
          inspector.documentValidator = documentValidator;
        }
        if (inspector.catalogSearchUrl !== catalogSearchUrl) {
          inspector.catalogSearchUrl = catalogSearchUrl;
        }
        const dataPresentation = this.inspectorDataPresentation?.modelRevision
          === this.engine?.modelRevision
          ? this.inspectorDataPresentation
          : null;
        if (inspector.dataPresentation !== dataPresentation) {
          inspector.dataPresentation = dataPresentation;
        }
        inspector.lootSort = this.lootSort;
        inspector.lootRateDisplayMode = this.lootRateDisplayMode;
      }
    }

    _inspectorDataPresentation(modelRevision, sankey) {
      const validRevision = typeof modelRevision === "string" && modelRevision.trim() !== "";
      if (!validRevision || !isRecord(sankey)) {
        return null;
      }
      const current = this.inspectorDataPresentation;
      if (current?.modelRevision === modelRevision && current.sankey === sankey) {
        return current;
      }
      return Object.freeze({ modelRevision, sankey });
    }

    _cacheInspectorDataPresentation(modelRevision, sankey) {
      this.inspectorDataPresentation = this._inspectorDataPresentation(modelRevision, sankey);
      return this.inspectorDataPresentation;
    }

    _handleProjection(event) {
      if (event.target !== this.engine || !isRecord(event.detail?.projection)) {
        return;
      }
      // The source reference is fixed for the active model and changes only at
      // the model-commit boundary. Reading the complete Datastar signal object
      // on every local projection would put page-state materialization on the
      // synchronous numeric-input path.
      this._syncActiveUserLayer();
      this._wireInspector();
      if (event.detail?.status === "validated" && isRecord(this.engine.userLayer)) {
        this.confirmedUserLayer = clone(this.engine.userLayer);
      }
      this._present(event.detail.projection);
      this._validateActivatedRevision();
    }

    _handleStatus(event) {
      if (event.target !== this.engine) {
        return;
      }
      const inspectors = this.document.querySelectorAll?.("fishy-calculator-layer-inspector-host") ?? [];
      for (const inspector of inspectors) {
        inspector.engineStatus = event.detail;
      }
      annotateOutputValidity(this.document, event.detail?.outputValidity ?? []);
      annotateInputIssues(this.document, event.detail?.issues ?? []);
      const repairs = event.detail?.repairReport?.repairs ?? [];
      if (event.detail?.status === "validated" && repairs.length > 0) {
        const recoveryKey = JSON.stringify([
          event.detail.modelRevision,
          repairs,
        ]);
        if (recoveryKey !== this.lastRecoveryEventKey) {
          this.lastRecoveryEventKey = recoveryKey;
          this.document.dispatchEvent?.(new global.CustomEvent(
            "fishystuff:calculator-catalog-recovered",
            {
              detail: Object.freeze({
                schemaVersion: 2,
                modelRevision: event.detail.modelRevision,
                repairReport: event.detail.repairReport,
                normalizedScenario: clone(this.engine.scenario),
              }),
            },
          ));
        }
      }
      if (event.detail?.status === "validated") {
        this._validateActivatedRevision();
      }
    }

    _handlePresentation(event) {
      if (event.target === this.engine && isRecord(event.detail?.presentation)) {
        const prepared = this._preparePresenter(
          event.detail.presentation,
          event.detail.modelRevision,
        );
        if (prepared === null) {
          event.preventDefault?.();
          return;
        }
        const revision = event.detail.presentationRevision;
        if (revision !== null && revision !== undefined) {
          try {
            revision.accept(prepared.participant);
          } catch (error) {
            try {
              prepared.participant.rollback();
            } catch (rollbackError) {
              this.engine?.failLocalEvaluation?.(new AggregateError(
                [error, rollbackError],
                "calculator presenter registration and rollback failed",
              ));
              event.preventDefault?.();
              return;
            }
            this.engine?.failLocalEvaluation?.(error);
            event.preventDefault?.();
          }
        } else {
          prepared.participant.activate();
        }
      }
    }

    _acceptedModelControlSignalProjection() {
      return {
        ...fishingSourceSignalPatch(this.engine?.model?.selection?.fishingSource),
        ...acceptedFixedControlSignalPatch(this.engine?.scenario),
      };
    }

    _handleModelRevisionActivated(event) {
      if (
        event.target !== this.engine
        || event.detail?.schemaVersion !== SCHEMA_VERSION
        || event.detail?.modelRevision !== this.engine?.modelRevision
      ) {
        return;
      }
      try {
        this.pendingCommittedModelSignalPatch = {
          expected: clone(this._acceptedModelControlSignalProjection()),
        };
      } catch (error) {
        this.pendingCommittedModelSignalPatch = null;
        this.engine?.failLocalEvaluation?.(error);
      }
    }

    _handleModelCommitted(event) {
      if (event.target !== this.engine || !isRecord(event.detail)) {
        return;
      }
      const sourceReference = requiredString(
        event.detail.sourceReference,
        "calculator source reference",
      );
      this.sourceReference = sourceReference;
      this.pendingCommittedModelSignalPatch = null;
      if (typeof this.patchSignals !== "function") {
        return;
      }
      let scenarioPatch;
      try {
        scenarioPatch = this._acceptedModelControlSignalProjection();
      } catch (error) {
        this.engine?.failLocalEvaluation?.(error);
        return;
      }
      const commitPatch = {
        _calculatorSourceReference: sourceReference,
        ...scenarioPatch,
      };
      this.pendingCommittedModelSignalPatch = {
        // Keep the complete accepted projection so both the model's control
        // patch and a later-delivered patch from the user transition that
        // requested it are causal acknowledgements. A genuinely different
        // value clears this guard before that user transition is evaluated.
        expected: clone(commitPatch),
      };
      // The pinned Datastar runtime can deliver the causal signal-patch
      // acknowledgements after this call returns and in a different order.
      this.patchSignals(commitPatch, { replaceTopLevel: true });
    }

    _handleLayerDocument(event) {
      const document = event.detail?.document;
      if (!isRecord(document?.userLayer) || !this.connect()) {
        return null;
      }
      const candidate = clone(document.userLayer);
      this.userLayer = candidate;
      this.engine.userLayer = this.userLayer;
      if (!sameValue(this.engine.userLayer, candidate)) {
        this._syncActiveUserLayer();
        this._wireInspector();
        return null;
      }
      this.userLayer = clone(this.engine.userLayer);
      const activated = this._userLayerDocument(this.userLayer);
      this._updateUserLayerPresetDocument(activated);
      this._wireInspector();
      this._refreshModelForUserLayerItems();
      return activated;
    }

    _handleConfirmedLayerDocument(event) {
      const document = event.detail?.document;
      if (!isRecord(document?.userLayer) || !this.connect()) {
        return null;
      }
      this.userLayer = clone(document.userLayer);
      this.confirmedUserLayer = clone(document.userLayer);
      const confirmed = this._userLayerDocument(this.userLayer);
      this._reconcileUserLayerPresetDocument(confirmed);
      this._wireInspector();
      this._refreshModelForUserLayerItems();
      return confirmed;
    }

    _handleSectionLayout(event) {
      if (typeof this.patchSignals !== "function" || !isRecord(event.detail)) {
        return;
      }
      const stacks = Array.from(
        this.document.querySelectorAll?.("fishy-calculator-section-stack") ?? [],
      );
      if (!stacks.includes(event.target)) {
        return;
      }
      const current = clone(this.getSignals()?._calculator_ui ?? {});
      const workspaceTab = requiredString(event.detail.workspaceTab, "calculator workspace tab");
      current.custom_layout = clone(event.detail.customLayout);
      current.custom_sections = clone(event.detail.customSections);
      this.patchSignals({ _calculator_ui: current }, { replaceTopLevel: true });
      if (workspaceTab !== current.workspace_tab) this.navigateWorkspace(workspaceTab);
    }

    _handleScenarioIntent(event) {
      if (event.target !== this.engine || !isRecord(event.detail)) {
        return;
      }
      this.handleScenarioIntent(event.detail.scenario, event.detail.patch);
    }

    _handleInspectorItemSelected(event) {
      if (
        !this.engine
        || !this.engine.contains?.(event.target)
        || event.detail?.modelRevision !== this.engine.modelRevision
      ) {
        return false;
      }
      if (event.detail?.itemId === null) {
        this.inspectorSelectedItemId = null;
        return true;
      }
      try {
        const [itemId] = canonicalItemIds(
          [event.detail?.itemId],
          "selected inspector item ID",
        );
        this.inspectorSelectedItemId = itemId;
        return true;
      } catch (error) {
        this.engine.failLocalEvaluation?.(error);
        return false;
      }
    }

    _handleRateAlternativeSelected(event) {
      if (
        !this.engine
        || !this.engine.contains?.(event.target)
        || event.detail?.modelRevision !== this.engine.modelRevision
      ) {
        return false;
      }
      try {
        const familyId = requiredString(
          event.detail?.familyId,
          "rate alternative family ID",
        );
        const alternativeId = event.detail?.alternativeId === null
          ? null
          : requiredString(
            event.detail?.alternativeId,
            "rate alternative ID",
          );
        const current = canonicalRateAlternativeOverrides(
          this.engine.scenario?.rateAlternativeOverrides ?? [],
        );
        const withoutFamily = current.filter((entry) => entry.familyId !== familyId);
        const next = canonicalRateAlternativeOverrides(alternativeId === null
          ? withoutFamily
          : [...withoutFamily, { familyId, alternativeId }]);
        if (sameValue(next, current)) {
          return false;
        }
        this.engine.scenario = scenarioWithRateAlternativeOverrides(
          this.engine.scenario,
          next,
        );
        if (typeof this.patchSignals === "function") {
          this.patchSignals(
            { [RATE_ALTERNATIVE_OVERRIDES_SIGNAL]: next },
            { replaceTopLevel: true },
          );
        }
        return true;
      } catch (error) {
        this.engine.failLocalEvaluation?.(error);
        return false;
      }
    }

    _handleModelItemsRequest(event) {
      if (
        !this.engine
        || !this.engine.contains?.(event.target)
        || event.detail?.modelRevision !== this.engine.modelRevision
      ) {
        return false;
      }
      try {
        if (
          !Array.isArray(event.detail?.itemIds)
          || !Array.isArray(event.detail?.customItemIds)
        ) {
          throw new TypeError(
            "calculator model item requests require itemIds and customItemIds arrays",
          );
        }
        const selected = canonicalItemIds(event.detail.itemIds);
        const custom = canonicalItemIds(
          event.detail.customItemIds,
          "Custom inspector item ID",
        );
        if (custom.some((itemId) => !selected.includes(itemId))) {
          throw new TypeError(
            "Custom inspector item IDs must be included in requested item IDs",
          );
        }
        this.inspectorCustomItemIds = Object.freeze([...custom]);
        const requested = this._desiredSupplementalItemIds(selected);
        if (
          typeof this.engine.pendingModelRequestId === "string"
          && sameValue(requested, this.pendingModelRequestedItemIds)
        ) {
          return false;
        }
        const active = canonicalItemIds(
          this.engine.model?.selection?.supplementalItemIds ?? [],
        );
        if (sameValue(requested, active)) {
          return false;
        }
        return this._requestModel(this.engine.scenario, requested);
      } catch (error) {
        this.engine.failLocalEvaluation?.(error);
        return false;
      }
    }

    _refreshModelForUserLayerItems() {
      const selected = this.inspectorSelectedItemId === null
        ? []
        : [this.inspectorSelectedItemId];
      const requested = this._desiredSupplementalItemIds(selected);
      const active = canonicalItemIds(
        this.engine?.model?.selection?.supplementalItemIds ?? [],
      );
      const missing = requested.filter((itemId) => (
        !this._modelLoadsItemId(itemId) && !active.includes(itemId)
      ));
      if (missing.length === 0) {
        return false;
      }
      if (
        typeof this.engine?.pendingModelRequestId === "string"
        && sameValue(requested, this.pendingModelRequestedItemIds)
      ) {
        return false;
      }
      return this._requestModel(this.engine.scenario, requested);
    }

    _preparePresenter(presentation, modelRevision = this.engine?.modelRevision) {
      if (!isRecord(presentation)) {
        return null;
      }
      const previousPresentation = this.staticPresentation;
      const previousInspectorDataPresentation = this.inspectorDataPresentation;
      const previousAdapter = this.presentationAdapter;
      let installed = false;
      try {
        const unchanged = this.presentationAdapter !== null
          && sameValue(presentation, this.staticPresentation);
        const candidateInspectorDataPresentation = this._inspectorDataPresentation(
          modelRevision,
          presentation.sankey,
        );
        let candidateAdapter = unchanged ? previousAdapter : null;
        if (unchanged) {
          installed = true;
        } else {
          const factory = global.FishystuffCalculatorProjectionPresenter?.create;
          if (typeof factory === "function") {
            candidateAdapter = factory({
              document: this.document,
              root: this.document,
              locale: requiredString(this.language().locale, "locale"),
              staticPresentation: presentation,
            });
            installed = true;
          }
        }
        let state = "prepared";
        const participant = Object.freeze({
          schemaVersion: 1,
          activate: () => {
            if (state !== "prepared") {
              throw new Error("calculator presenter revision is not prepared for activation");
            }
            state = "active";
            this.staticPresentation = presentation;
            this.inspectorDataPresentation = candidateInspectorDataPresentation;
            this.presentationAdapter = candidateAdapter;
            if (
              candidateAdapter
              && this.latestProjection
              && this.latestProjection.modelRevision === modelRevision
            ) {
              this._present(this.latestProjection);
            }
            this._wireInspector();
          },
          rollback: () => {
            if (state === "rolledBack") {
              return;
            }
            const wasActive = state === "active";
            state = "rolledBack";
            if (!wasActive) {
              return;
            }
            this.staticPresentation = previousPresentation;
            this.inspectorDataPresentation = previousInspectorDataPresentation;
            this.presentationAdapter = previousAdapter;
            try {
              if (previousAdapter && this.latestProjection) {
                previousAdapter.present(this.latestProjection, {
                  lootRateDisplayMode: this.lootRateDisplayMode,
                });
              }
            } finally {
              this._wireInspector();
            }
          },
        });
        return Object.freeze({ installed, participant });
      } catch (error) {
        this.engine?.failLocalEvaluation?.(error);
        return null;
      }
    }

    _installPresenter(presentation, modelRevision = this.engine?.modelRevision) {
      const prepared = this._preparePresenter(presentation, modelRevision);
      if (prepared === null) {
        return false;
      }
      prepared.participant.activate();
      return prepared.installed;
    }

    _present(projection) {
      this.latestProjection = projection;
      let view = null;
      try {
        view = this.presentationAdapter?.present(projection, {
          lootRateDisplayMode: this.lootRateDisplayMode,
        }) ?? null;
      } catch (error) {
        this.engine?.failLocalEvaluation?.(error);
      }
      this.onProjection?.(projection, view);
    }

    _validateActivatedRevision() {
      const revision = this.engine?.modelRevision;
      if (
        typeof revision !== "string"
        || this.lastBoundaryRevision === revision
        || !isRecord(this.userLayer)
        || typeof this.sourceReference !== "string"
      ) {
        return false;
      }
      const initialActivation = this.initialModelRevision === null;
      this.initialModelRevision ??= revision;
      this.lastBoundaryRevision = revision;
      if (initialActivation && this.boundaryInspectorPresent) {
        return false;
      }
      const boundary = initialActivation ? "initialization" : "modelRevision";
      void this.validateUserLayer({ boundary, userLayer: this.userLayer }).catch(() => {});
      return true;
    }
  }

  global.FishyCalculatorEnginePage = Object.freeze({
    CalculatorEnginePageController,
    create(options) {
      return new CalculatorEnginePageController(options);
    },
  });
})(typeof window === "undefined" ? globalThis : window);
